import I18n from 'I18n';
import { RECURRING_BILLING_FREQUENCY, TERM } from 'customer-data-objects/lineItem/PropertyNames';
import { ANNUALLY, BILLING_FREQUENCIES_OBJECT } from 'customer-data-objects/product/BillingFrequency';
import { getMonthsPerBillingFrequency, getMonthsPerPeriod, isWeeklyOrBiweeklyBillingFrequency } from 'customer-data-properties/v2/utils/period';
import { calculateBaseDiscountedLineItemPrice, calculateLineItemDiscount, getDiscountType, getSelectedDiscountValue } from 'products-ui-components/utils/calculations/discounts';
import { getTermHasLength } from 'products-ui-components/utils/calculations/terms';
import { getDecimalPrecision } from 'products-ui-components/utils/currency';
import getLineItemProperties from 'products-ui-components/utils/properties/getLineItemProperties';
import { isRecurring, sortByBillingFrequency } from 'products-ui-components/utils/recurrence';
import { getProperty } from '../objectAccessors';
import { getLineItem } from '../subject';

// ******************************
// * Recurring Billed Line Items*
// ******************************
/**
 * @deprecated use customer-data-properties/utils/period's getMonthsPerBillingFrequency
 */
export function getMonthsPerRatePeriod(rate) {
  var _rateInfo$months;
  const rateInfo = BILLING_FREQUENCIES_OBJECT[rate];
  return (_rateInfo$months = rateInfo === null || rateInfo === void 0 ? void 0 : rateInfo.months) !== null && _rateInfo$months !== void 0 ? _rateInfo$months : 0;
}

/**
 * Retrieves the billing frequency from the given number of months.
 *
 * @param months - The number of months.
 * @returns The corresponding billing frequency, or undefined if not found.
 */
export function getBillingFrequencyFromMonths(months) {
  for (const [key, value] of Object.entries(BILLING_FREQUENCIES_OBJECT)) {
    if ('months' in value && value.months === months) {
      return key;
    }
  }
  return undefined;
}

/**
 * Retrieves the billing frequency from the given number of weeks.
 *
 * @param weeks - The number of weeks to retrieve the billing frequency from.
 * @returns The corresponding billing frequency as a string, or undefined if no match is found.
 */
export function getBillingFrequencyFromWeeks(weeks) {
  for (const [key, value] of Object.entries(BILLING_FREQUENCIES_OBJECT)) {
    if ('weeks' in value && value.weeks === weeks) {
      return key;
    }
  }
  return undefined;
}
export const calculateProRateFactor = (lineItem, billingFrequency) => {
  /*
   * If a a line item is billed per-month and its term is 8 months and the deal
   * is set to report revenue annually, the pro rate factor is 8/12 since revenue
   * for the recurring line item is earned only 8 of the 12 possible months.
   */
  const term = getProperty(lineItem, TERM);
  const monthsPerReportPeriod = getMonthsPerBillingFrequency(billingFrequency);
  const monthsPerTerm = getMonthsPerPeriod(term);
  if (!getTermHasLength(term) || monthsPerTerm && monthsPerTerm >= monthsPerReportPeriod) {
    return 1;
  }
  return monthsPerTerm ? monthsPerTerm / monthsPerReportPeriod : 0;
};
export const calculateReportPeriodRatio = (maybeLineItem, dealReportFrequency) => {
  /*
   * If a line item is billed per-quarter with a term of 8 months, and the
   * customer requests a annual revenue report: This will return a ratio of
   * 12/3 * 8/12.
   */

  const lineItem = getLineItem(maybeLineItem);
  const recurringLineItemBillingFrequency = getProperty(lineItem, RECURRING_BILLING_FREQUENCY);

  /**
   * TODO: Look to fix this when we add support for weekly and bi-weekly billing frequency calculations.
   * For now we are passing 0 as we don't have a good representation of how many weeks in a month.
   * This is only used for legacy deal calculations.
   *  */
  if (isWeeklyOrBiweeklyBillingFrequency(recurringLineItemBillingFrequency)) {
    return 0;
  }
  const monthsPerReportPeriod = getMonthsPerBillingFrequency(dealReportFrequency);
  const monthsPerItemBillingPeriod = getMonthsPerBillingFrequency(recurringLineItemBillingFrequency);
  return monthsPerReportPeriod / monthsPerItemBillingPeriod * calculateProRateFactor(lineItem, dealReportFrequency);
};

// Pro-rated line item price over the deal's reporting frequency AFTER discounts
export const calculateRecurringDiscountedLineItemPrice = (lineItem, billingFrequency) => {
  if (!isRecurring(lineItem)) {
    return 0;
  }
  const {
    discount,
    discountPercentage,
    price,
    quantity,
    lineItemCurrencyCode
  } = getLineItemProperties(lineItem);
  const discountType = getDiscountType(discount, discountPercentage);
  const discountValue = getSelectedDiscountValue(discount, discountPercentage, discountType);
  const decimalDigits = getDecimalPrecision(I18n.currencySymbols, lineItemCurrencyCode);
  const pricePerItemBillingPeriod = calculateBaseDiscountedLineItemPrice( /**@FIXME emitted-types convert to number rather than just casting, if it's safe */
  quantity, price, discountValue, discountType, decimalDigits);
  const ratio = calculateReportPeriodRatio(lineItem, billingFrequency);
  return pricePerItemBillingPeriod * ratio;
};

/**
 * Calculates the recurring subtotal for a given array of line items or map of
 * line items for the specified billing frequency.
 * Total calculated price does not include costs (to the HubSpot
 * customer) of these line items.
 *
 * @param lineItems - The array of line items or map of line items.
 * @param billingFrequency - The billing frequency for the line items. Defaults to ANNUALLY.
 * @returns The total discounted subtotal of all recurring billed line items,
 * per the period of time specified.
 */
export const calculateRecurringSubtotal = (lineItems, billingFrequency = ANNUALLY) => {
  return [...lineItems.values()].reduce((subtotal, lineItem) => {
    return subtotal + calculateRecurringDiscountedLineItemPrice(lineItem, billingFrequency);
  }, 0);
};
export function getAllBilledFrequencies(lineItems) {
  return new Set([...lineItems.values()].filter(lineItem => isRecurring(lineItem)).map(lineItem => getProperty(lineItem, RECURRING_BILLING_FREQUENCY)).sort((a, b) => sortByBillingFrequency(a, b)));
}

/**
 * Returns the total discount amount for all recurring billed line items
 * that are billed for a particular frequency.
 * It will filter out passed-in line items that aren't billed for that frequency.
 *
 * @param {List} lineItems - The lineItems for which to calculate total discount
 * @param {string} frequency - The billing frequency of line items whose discounts to total.
 * @return {number} The total discount amount for all line items billed at given frequency.
 */
const getDiscountPerFrequency = (lineItems, frequency) => {
  return [...lineItems.values()].reduce((acc, lineItem) => {
    const {
      recurringBillingFrequency
    } = getLineItemProperties(lineItem);
    if (recurringBillingFrequency === frequency) {
      return acc + calculateLineItemDiscount(lineItem);
    }
    return acc;
  }, 0);
};

/**
 * Returns the total discounted price amount for all recurring billed line items
 * that are billed for a particular frequency.
 * It will filter out passed-in line items that aren't billed for that frequency.
 *
 * @param {Map<number, LineItem>} lineItems - The lineItems for which to calculate total discounted price
 * @param {string} frequency - The billing frequency of line items whose prices to total.
 * @return {number} The total discounted price amount for all line items billed at given frequency.
 */
const getSubtotalPerFrequency = (lineItems, frequency) => {
  return [...lineItems.values()].reduce((acc, lineItem) => {
    const {
      recurringBillingFrequency
    } = getLineItemProperties(lineItem);
    if (recurringBillingFrequency === frequency) {
      return acc + calculateRecurringDiscountedLineItemPrice(lineItem, frequency);
    }
    return acc;
  }, 0);
};

/**
 * Returns the total discounted price amount, and discount, for all recurring billed line
 * items, grouped by frequency.
 *
 * @param {Map<number, LineItem>} lineItems - The lineItems for which to calculate total discounted price and discount.
 * @return {List(Map)} - A List of Maps for each frequency that line items are billed for.
                         Maps reference the frequency, total discount, and total discounted
                         price of all line items billed for that frequency.
 */

export function getTotalsPerFrequency(lineItemsMap) {
  const billedFrequencies = [...getAllBilledFrequencies(lineItemsMap).values()];
  return billedFrequencies.map(frequency => ({
    frequency,
    discount: getDiscountPerFrequency(lineItemsMap, frequency),
    subtotal: getSubtotalPerFrequency(lineItemsMap, frequency)
  }));
}